import {useEffect, useMemo, useRef, useState } from 'react';
import { FilterListType, OptionType, SelectedFilter } from '../types';
import './AdvanceFilterModal.scss';
import CustomFilterSelect from './CustomFilterSelect';
import { ApplyButton, FilterDeleteSvg } from '../../../command_center/components/center/modals/console/ConsoleSvg';
import { showToastMessage } from '../../../../utils/commonFunctions';
import CustomToolTip from '../../CustomToolTip';

interface AdvanceFilterModalProps {
    onSelect: (name: string, value: string | {
        label: string;
        value: any;
    } | number | boolean) => void;
    filterList: FilterListType | null
    selectedFilter: SelectedFilter | null;
    setSelectedFilters: (value: SelectedFilter | null) => void;
    appliedFilters: SelectedFilter[];
    resetFilter: () => void;
    applyFilter: () => void;
    editingFilter: SelectedFilter | null;
    setEditingFilter: (value: SelectedFilter | null) => void;
    openOptions: 'Property' | 'Operator' | 'Value' | null;
    setOpenOptions: (value: 'Property' | 'Operator' | 'Value' | null) => void;
    popupPosition: { top: number, left: number, right: number }
    inputValue: string;
    setInputValue: (value: string) => void;
    filterCase?: string;
    containerRef?: React.RefObject<HTMLDivElement>;
}

const AdvanceFilterModal: React.FC<AdvanceFilterModalProps> = ({ onSelect, filterList, selectedFilter, setSelectedFilters, resetFilter, applyFilter, appliedFilters, editingFilter, setEditingFilter, openOptions, setOpenOptions, popupPosition, inputValue, setInputValue, filterCase, containerRef }) => {
    const operatorsOptions = useMemo(() => filterList?.options.find(option => option.label === selectedFilter?.properties)?.operator?.options || [], [filterList, selectedFilter])
    const operatorsPlaceholder = useMemo(() => filterList?.options.find(option => option.label === selectedFilter?.properties)?.operator?.placeholder || 'Operator', [filterList, selectedFilter])
    const activeOperatorSearch = useMemo(() => filterList?.options.find(option => option.label === selectedFilter?.properties)?.operator?.enabledSearch || false, [filterList, selectedFilter])
    const operatorMultiselection = useMemo(() => filterList?.options.find(option => option.label === selectedFilter?.properties)?.operator?.multiselection || false, [filterList, selectedFilter])

    const valueOptions = useMemo(() => filterList?.options.find(option => option.label === selectedFilter?.properties)?.properties?.options || [], [filterList, selectedFilter])
    const valuePlaceholder = useMemo(() => filterList?.options.find(option => option.label === selectedFilter?.properties)?.properties?.placeholder || 'Value', [filterList, selectedFilter])
    const valuePropertySearch = useMemo(() => filterList?.options.find(option => option.label === selectedFilter?.properties)?.properties?.enabledSearch || false, [filterList, selectedFilter])
    const valueMultiselection = useMemo(() => filterList?.options.find(option => option.label === selectedFilter?.properties)?.properties?.multiselection || false, [filterList, selectedFilter])
    const valueTextField = useMemo(() => {
        const valueField = filterList?.options.find(
            option => option.label === selectedFilter?.properties
        )?.properties?.valueField;

        return valueField === "text" || valueField === "range";
    }, [filterList, selectedFilter]);

    // Clear value when property changes (e.g., switching to/from TC/TM ID)
    const prevPropertyRef = useRef<string | undefined>(selectedFilter?.properties);
    useEffect(() => {
        const current = selectedFilter?.properties;
        const previous = prevPropertyRef.current;
        if (previous !== undefined && current !== previous) {
            setInputValue("");
        }
        prevPropertyRef.current = current;
    }, [selectedFilter?.properties]);

    const properties = useMemo(() => appliedFilters?.length ? appliedFilters?.map(filter => filter.properties) : [], [appliedFilters])
    const propertiesOptions = useMemo(() => {
        if (!filterList?.options) return [];

        if (appliedFilters?.length) { //code for adding the property to options while editing and we change the property
            const filtered = filterList.options.filter(op => !properties.includes(op.label));

            if (editingFilter && selectedFilter?.properties !== editingFilter.properties) {
                const matchedOption = filterList.options.find(
                    (op) => op.label === editingFilter.properties
                );

                if (matchedOption && !filtered.some(op => op.label === matchedOption.label)) {
                    return [...filtered, matchedOption];
                }
            }
            return filtered;
        }
        return filterList.options;
    }, [appliedFilters, filterList, editingFilter, selectedFilter]);

    useEffect(() => {
        const modal = containerRef?.current?.querySelector('.advance__filter__modal__container');
        const appliedFilterContainer = containerRef?.current?.querySelector('.applied__filters__container');
        const filterIconContainer = containerRef?.current?.querySelector('.filter__icon__container');

        const clickedOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            const isClickInsideDropdown = target.closest('.custom__filter__select');
            const isClickInsideModal = modal?.contains(target);
            const isClickInsideFilters = appliedFilterContainer?.contains(target);
            const isClickInsideIcon = filterIconContainer?.contains(target);

            const isDropdownOpen = openOptions !== null;

            if (isDropdownOpen && !isClickInsideDropdown) {
                // Close only the dropdown
                setOpenOptions(null);
                return;
            }

            if (!isClickInsideModal && !isClickInsideFilters && !isClickInsideIcon) {
                // Dropdown not open, clicked outside everything → close modal
                modal?.classList.remove('active__modal');
                setInputValue('')
                setEditingFilter(null);
            }
        };

        document.addEventListener('mousedown', clickedOutside);
        return () => {
            document.removeEventListener('mousedown', clickedOutside);
        };
    }, [openOptions, containerRef]);

    const handleBlur = () => {
        let parts = inputValue.split(",").map((v) => v.trim()).filter(Boolean); //indivisual ids
        if (parts?.length > 1) {
            const last = parts[parts.length - 1]; // current being typed
            const prev = parts.slice(0, -1);      // already finished values

            // If user just typed a comma and last value is duplicate → remove it
            if (prev.includes(last)) {
                showToastMessage("error", `Removed duplicate value ${last}`);
                parts = prev; // drop the duplicate
                const newinputValue = parts.join(", ");
                setInputValue(newinputValue)
                // Update state with valid IDs
                const updated = newinputValue.split(",").map((v) => v.trim()).filter(Boolean);
                setSelectedFilters({
                    ...selectedFilter,
                    value: updated,
                } as SelectedFilter);
            }
        }
    }

    const previousInputRef = useRef<string>("");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let rawValue = e.target.value;
        // Track previous value to detect backspace over comma/space
        const prev = previousInputRef.current || "";
        // Basic cleanup (invalid chars, multiple commas/spaces)
        let cleanedValue = rawValue.replace(/[^0-9, ]/g, "");
        // Prevent multiple commas
        cleanedValue = cleanedValue.replace(/,+/g, ",");
        // Prevent multiple spaces
        cleanedValue = cleanedValue.replace(/\s+/g, " ");
        // Disallow space at start
        cleanedValue = cleanedValue.replace(/^\s+/, "");
        // Disallow space after number
        cleanedValue = cleanedValue.replace(/(\d+) +/g, "$1");
        // Disallow comma unless it follows a number
        cleanedValue = cleanedValue.replace(/([^0-9]),/g, "$1");
        cleanedValue = cleanedValue.replace(/^,/, "");// no comma at start
        // Split values
        let parts = cleanedValue.split(",").map((v) => v.trim());
        // :key: Remove leading zeros but preserve typing flow
        parts = parts.map((val, idx) => {
            if (/^\d+$/.test(val)) {
                return String(Number(val)); // 00088 → 88
            }
            return val;
        }).filter(Boolean);
        // Range validation
        if (parts.some((id) => {
            const num = Number(id);
            return num < 0 || num > 65535;
        })) {
            showToastMessage("info", "TC/TM IDs must be between 0 and 65535");
            return;
        }
        // Remove duplicates only if value finished with comma
        if (parts.length > 1) {
            const last = parts[parts.length - 1];
            const prevParts = parts.slice(0, -1);
            if (cleanedValue.endsWith(",") && prevParts.includes(last)) {
                showToastMessage("error", `Removed duplicate value ${last}`);
                parts = prevParts;
            }
        }
        // :key: Preserve user's comma typing while allowing backspace over comma/space
        const isBackspacingSpaceAfterComma = prev.endsWith(", ") && rawValue.endsWith(",") && rawValue.length === prev.length - 1;
        const isBackspacingComma = prev.endsWith(",") && rawValue === prev.slice(0, -1);
        let displayValue = parts.join(", ");
        if (parts.length > 0) {
            if (isBackspacingSpaceAfterComma) {
                // User removed the space after comma → keep the comma
                displayValue += ",";
            } else if (!isBackspacingComma) {
                const endsWithCommaOrSpaces = /,[ ]+$/.test(rawValue) || /,$/.test(rawValue);
                const invalidTypedAfterComma = /,[ ]*[^0-9, ]$/.test(rawValue); // '.' or letter after comma
                if (endsWithCommaOrSpaces || invalidTypedAfterComma) {
                    const hadSpaces = /,[ ]+[^]*$/.test(rawValue) || /,[ ]+$/.test(rawValue);
                    displayValue += hadSpaces ? ", " : ",";
                }
            }
        }
        setInputValue(displayValue);
        previousInputRef.current = displayValue;
        setSelectedFilters({
            ...selectedFilter,
            value: parts,
        } as SelectedFilter);
    };

    useEffect(() => {
        if (!valueTextField) return;
        if (!editingFilter) return;

        // Only prefill when the currently selected property matches the filter being edited
        if (selectedFilter?.properties === editingFilter.properties) {
            setInputValue(
                Array.isArray(editingFilter?.value)
                    ? editingFilter.value.join(", ")
                    : (editingFilter?.value as string) || ""
            );
        } else {
            // Property changed during edit → clear the input and selected value
            setInputValue("");
            setSelectedFilters({
                ...(selectedFilter as SelectedFilter),
                value: [],
            } as SelectedFilter);
        }
    }, [editingFilter, valueTextField, selectedFilter?.properties])

    const containerStyle: React.CSSProperties = filterCase === 'validate' ? {
        position: 'fixed',
        top: popupPosition.top,
        right: popupPosition.right,
        zIndex: 1000,
    } : filterCase === 'report' ? {
        position: 'absolute',
        top: 32,
        right: 0,
        left: 'auto',
        zIndex: 1000,
    } : {
        position: 'absolute',
        top: popupPosition.top,
        left: popupPosition.left - 900,
        zIndex: 1000,
    };

    return (
        <div className='advance__filter__modal__container' style={containerStyle}>
            <div className='advance__filter__modal__options'>
                <CustomFilterSelect
                    options={propertiesOptions as OptionType[]}
                    activeSearch={filterList?.enabledSearch}
                    placeholder={filterList?.placeholder || ''}
                    selectedValue={selectedFilter?.properties || null}
                    onSelect={(value) => { onSelect('properties', value) }}
                    // disabled={appliedFilters?.some((s) => s.properties === selectedFilter?.properties)}
                    setOpenOptions={setOpenOptions}
                    openOptions={openOptions}
                    selectedFilter={selectedFilter}
                />
                <CustomFilterSelect
                    options={operatorsOptions as OptionType[]}
                    placeholder={operatorsPlaceholder}
                    activeSearch={activeOperatorSearch}
                    multiSelection={operatorMultiselection}
                    selectedValue={selectedFilter?.operator || null}
                    onSelect={(value) => { onSelect('operator', value) }}
                    disabled={!selectedFilter ? true : false}
                    setOpenOptions={setOpenOptions}
                    openOptions={openOptions}
                    selectedFilter={selectedFilter}
                />
                {!valueTextField &&
                    <CustomFilterSelect
                        options={valueOptions as OptionType[]}
                        placeholder={valuePlaceholder}
                        activeSearch={valuePropertySearch}
                        multiSelection={valueMultiselection}
                        selectedValue={
                            Array.isArray(selectedFilter?.value)
                                ? selectedFilter.value.length > 0
                                    ? selectedFilter.value
                                    : null
                                : selectedFilter?.value ?? null
                        }
                        onSelect={(value) => { onSelect('value', value) }}
                        disabled={!selectedFilter ? true : false}
                        setOpenOptions={setOpenOptions}
                        openOptions={openOptions}
                        selectedFilter={selectedFilter}
                    />
                }
                {(valueTextField) &&
                    <CustomToolTip title={inputValue} disabled={!inputValue || inputValue === ''}>
                        <input
                            type="text"
                            placeholder="Enter Value"
                            value={inputValue}
                            onChange={handleChange}
                            className="advance_filter_custom_text_input"
                            onMouseDown={(e) => { e.stopPropagation() }}
                            onBlur={handleBlur}
                        />
                    </CustomToolTip>
                }
                <div className="advance__filter__modal">
                    <div style={{ cursor: (selectedFilter?.value && selectedFilter?.value?.length > 0) ? '' : 'not-allowed' }}>
                        <button className={`advance__filter__modal__button delete__filter__button ${(selectedFilter?.value && selectedFilter?.value?.length > 0) ? 'active' : 'disabled'}`} onClick={resetFilter}>
                            <FilterDeleteSvg />
                        </button>
                    </div>
                    <div style={{ cursor: (selectedFilter?.value && selectedFilter?.value?.length > 0) ? '' : 'not-allowed' }}>
                        <button className={`advance__filter__modal__button apply__filter__button ${(selectedFilter?.value && selectedFilter?.value?.length > 0) ? 'active' : 'disabled'}`} onClick={applyFilter}>
                            <ApplyButton />
                        </button>
                    </div>
                </div>
            </div>
            {selectedFilter?.properties === 'TC/TM ID' && <div className='tc-tm-id-info-text'>Add multiple values using comma</div>}
        </div>
    )
}

export default AdvanceFilterModal